import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { DSSetup, Service } from 'webapp-types';
import { BlockVisualization, Direction, PopupEvent } from 'webapp/enums/enums';
import { isAlive } from 'webapp/utils/common';

import highlightResult from 'webapp/helpers/highlight-result';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import MetricLibraryPopupsPopupMetricComponent from 'webapp/components/metric-library/popups/popup-metric';
import ToolTipComponent from 'webapp/components/tool-tip';
import ToolTipWithIconComponent from 'webapp/components/tool-tip-with-icon';
import VerificationBadgeComponent from 'webapp/components/verification/badge';

import type { Metric } from 'webapp/models-custom';

import type { NewBlockData } from 'webapp/services/model-manager/blocks';

export interface Args {
	model: Metric;
	typedFilter: string;
	onConnect: (sourceTypeKey: string) => void;
	addToBoard: (blockData: NewBlockData) => void;
}

export default class DesignerLeftPanelMetricsMetricComponent extends Component<Args> {
	@service('billing') declare billingService: Service.Billing;
	@service('popup') declare popupService: Service.Popup;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	@tracked isInfoPopupOpen = false;

	get sourcesText() {
		return this.args.model.sourcesText;
	}

	get showEditButton() {
		return !this.isDisabled && this.args.model.canEdit;
	}

	get showAddToBoardButton() {
		return !this.isDisabled;
	}

	@cached
	get isDisabled() {
		return this.args.model.missingBillingFeature || this.args.model.isPredefinedOnGenericSource;
	}

	get isFavorite() {
		return this.args.model.isFavorite;
	}

	get metricTypeForTracking() {
		let metricType = 'basic';
		if (this.args.model.isTypeMetricCalculationOrGeneric) {
			metricType = 'script';
		} else if (this.args.model.isTypeCustomQueryOrGeneric) {
			metricType = 'query';
		}
		return metricType;
	}

	get firstSourceAvatar() {
		return this.args.model.sourceAvatar;
	}

	@action
	edit() {
		const metric = this.args.model;
		if (metric) {
			metric.edit('designer');
		}
	}

	@action
	openInfoPopup(e: PointerEvent) {
		if (this.isInfoPopupOpen) {
			return;
		}

		const node = e.target as HTMLElement;

		if (!node) {
			return;
		}

		const activePopup = this.popupService.activePopup;

		// need to close previous and open a new one
		let beforeOpen;
		if (activePopup && this.popupService.isOpen(MetricLibraryPopupsPopupMetricComponent)) {
			beforeOpen = activePopup.close();
		} else {
			beforeOpen = Promise.resolve();
		}

		return beforeOpen.then(() => {
			const popup = this.popupService.createContext(
				MetricLibraryPopupsPopupMetricComponent,
				node,
				{
					popupClass: 'source-metric-popup',
					offset: -4,
					directions: [Direction.RIGHT, Direction.DOWN, Direction.UP, Direction.LEFT],
					allowStacked: true, // for bubble popup
				},
			);

			popup.open({
				model: this.args.model,
				showDragNDrop: true,
				showActions: true,
				onConnect: () => {
					this.connect();
				},
				onCreateBlock: () => {
					this.addToBoard();
				},
				trackingOrigin: 'designer library',
			});

			this.isInfoPopupOpen = true;
			popup.on(PopupEvent.CLOSED, this, () => {
				if (isAlive(this)) {
					this.isInfoPopupOpen = false;
				}
			});

			return popup;
		});
	}

	@action
	addToBoard() {
		const metric = this.args.model;

		let promise: Promise<DSSetup.MetricSetupData>;

		if (metric.spaceAccessId) {
			promise = this.mmMetricsService.resolveGenericMetricWithSpecific(
				metric.key,
				metric.spaceAccessId,
			);
		} else {
			promise = Promise.resolve(metric.metricSetupData!);
		}

		void promise.then((metricSetupData) => {
			const data: NewBlockData = {
				fromMetricSetup: metricSetupData,
				metricType: this.metricTypeForTracking,
				visualization: BlockVisualization.BAR_CHART,
			};
			this.args.addToBoard(data);
		});
	}

	@action
	connect() {
		const sourceTypeKey = this.args.model.dataSourceTypeKey;
		if (sourceTypeKey) {
			this.args.onConnect(sourceTypeKey);
		}
	}
	<template>
		<li
			class="metric-item datablock
				{{if this.isDisabled 'disabled' 'ui-draggable'}}
				{{if this.isInfoPopupOpen 'popup-opened'}}"
			data-metricsetupkey={{@model.metricData.key}}
			data-metricsetupspaceaccessid={{@model.metricData.spaceAccessId}}
			data-metrictype={{this.metricTypeForTracking}}
		>
			<div class="item__data-ct">
				<div class="item__sources">
					<img src={{this.firstSourceAvatar}} alt />
				</div>
				<div class="item__title">

					<strong class="text">
						<span>
							{{highlightResult text=@model.name match=@typedFilter}}
						</span>
					</strong>

					{{#if @model.isTypeMetricCalculationOrGeneric}}
						<CommonUiIconComponent @name="Calculator" @size="xs" @class="icon-type" />
					{{else if @model.isTypeCustomQueryOrGeneric}}
						<CommonUiIconComponent @name="Data" @size="xs" @class="icon-type" />
						{{#if @model.allowDimensions}}
							<CommonUiIconComponent
								@name="Breakdown"
								@size="sm"
								@class="icon-type"
								title="Breakdown by dimension(s) available."
							/>
						{{/if}}
					{{/if}}

					<VerificationBadgeComponent
						@verification={{@model.verificationInfo}}
						@entityLabel="metric"
						@size="sm"
					/>
					{{#if this.isFavorite}}
						<CommonUiIconComponent @name="StarFill" @size="sm" @class="icon-fav" />
					{{/if}}

				</div>
				<div class="item__subtitle">

					<span class="text">
						<span>
							{{highlightResult text=this.sourcesText match=@typedFilter}}
						</span>
					</span>

					{{#if @model.source.isStatusActionable}}
						<span class="status">
							<ToolTipWithIconComponent
								@text={{{@model.source.statusDescriptionWithLinks}}}
								@iconName="AlertFill"
								@maxWidth={{230}}
							/>
						</span>
					{{/if}}

				</div>
			</div>

			<div class="item__actions-ct">

				{{#if this.showEditButton}}
					<a
						role="button"
						class="mk-edit-button mk-edit-button--dark"
						{{on "click" this.edit}}
					>Edit
					</a>
				{{/if}}

				<span
					role="button"
					class="info-button"
					{{on "click" this.openInfoPopup}}
				><CommonUiIconComponent @name="InfoFill" @size="sm" />
				</span>

				{{#if this.showAddToBoardButton}}
					<a class="add" {{on "click" this.addToBoard}}>
						<CommonUiIconComponent @name="Plus" @size="md" />
					</a>
				{{/if}}

			</div>

			{{#unless this.isDisabled}}
				<ToolTipComponent
					@text="<img src='/images/arrow-drag.png' height='9'>Drag and drop a datablock to the canvas on the right."
					@position="right"
					@extraClass="drag-tooltip"
					@maxWidth={{185}}
					@delay={{2000}}
				/>
			{{/unless}}

		</li>
	</template>
}
